import { useEffect, useMemo, useState } from 'preact/hooks';
import type {
  Bucket,
  Currency,
  Deltas,
  HoldingRow,
  TimeWindow,
} from '../../shared/types';
import { fmtPct, fmtSignedPct } from '../../shared/format';
import { useMask } from '../lib/mask';
import { mergeSameIndex } from '../lib/merge';
import { EditHoldingPopover } from './EditHoldingPopover';
import { EditNonTradablePopover } from './EditNonTradablePopover';
import { AddHoldingDialog } from './AddHoldingDialog';
import './../styles/components/holdings.css';

const WK: Record<TimeWindow, keyof Deltas> = {
  '1D': 'd1',
  '7D': 'd7',
  '30D': 'd30',
  '90D': 'd90',
  '1Y': 'y1',
  '2Y': 'y2',
};
const WIN_LABEL: Record<TimeWindow, string> = {
  '1D': '1d',
  '7D': '7d',
  '30D': '30d',
  '90D': '90d',
  '1Y': '1y',
  '2Y': '2y',
};

type Props = {
  rows: HoldingRow[];
  ccy: Currency;
  win: TimeWindow;
  buckets: Bucket[];
  bucketFilter: string | null;
  onBucketFilterChange: (id: string | null) => void;
  onHoldingSaved?: () => void;
};

const INITIAL = 10;

function signClass(pct: number): string {
  if (pct === 0) return 'flat';
  return pct > 0 ? 'gain' : 'loss';
}

type AssetClassKey = NonNullable<HoldingRow['asset_class']>;

const CLASS_LABEL: Record<AssetClassKey, string> = {
  equity: 'Equity',
  crypto_volatile: 'Crypto',
  bond: 'Bond',
  cash_eq: 'Cash',
  gold: 'Gold',
};
const CLASS_KEY: Record<AssetClassKey, string> = {
  equity: 'equity',
  crypto_volatile: 'crypto',
  bond: 'bond',
  cash_eq: 'cash',
  gold: 'gold',
};

function isCryptoUnit(row: HoldingRow): boolean {
  if (row.asset_class === 'crypto_volatile') return true;
  if (row.asset_class === 'cash_eq') return true;
  return row.bucket_id.startsWith('crypto');
}

const VIRTUAL_BUCKET_TO_NON_TRADABLE_ID: Record<string, string> = {
  real_estate: 'real_estate',
  alternatives: 'artwork',
  private_equity: 'private_equity',
};
function isVirtualRow(row: HoldingRow): boolean {
  return row.bucket_id in VIRTUAL_BUCKET_TO_NON_TRADABLE_ID;
}
function virtualNonTradableId(row: HoldingRow): string | null {
  return VIRTUAL_BUCKET_TO_NON_TRADABLE_ID[row.bucket_id] ?? null;
}

function yahooUrl(row: HoldingRow): string | null {
  if (row.provider !== 'yahoo' || !row.provider_symbol) return null;
  return `https://finance.yahoo.com/quote/${encodeURIComponent(row.provider_symbol)}`;
}

export function HoldingsTable({
  rows,
  ccy,
  win,
  buckets,
  bucketFilter,
  onBucketFilterChange,
  onHoldingSaved,
}: Props) {
  const { amount, quantity } = useMask();
  const [expanded, setExpanded] = useState(false);
  const [editKey, setEditKey] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [allocMode, setAllocMode] = useState<'portfolio' | 'bucket'>('portfolio');
  const [mergeIdx, setMergeIdx] = useState(false);

  useEffect(() => {
    if (!bucketFilter) setAllocMode('portfolio');
  }, [bucketFilter]);

  function rowKey(r: HoldingRow): string {
    return `${r.bucket_id}:${r.symbol}`;
  }

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (b.value_usd ?? 0) - (a.value_usd ?? 0)),
    [rows]
  );

  const filtered = useMemo(
    () => bucketFilter ? sortedRows.filter((r) => r.bucket_id === bucketFilter) : sortedRows,
    [sortedRows, bucketFilter]
  );

  const displayRows = useMemo(
    () => (mergeIdx && !bucketFilter ? mergeSameIndex(filtered) : filtered),
    [filtered, mergeIdx, bucketFilter]
  );

  const canExpand = displayRows.length > INITIAL;
  const visible = expanded ? displayRows : displayRows.slice(0, INITIAL);

  const sortedBuckets = useMemo(
    () => [...buckets].sort((a, b) => b.value_usd - a.value_usd),
    [buckets]
  );

  const activeBucketName = useMemo(
    () => (bucketFilter ? buckets.find((b) => b.id === bucketFilter)?.name ?? null : null),
    [buckets, bucketFilter]
  );
  const allocScopeLabel =
    allocMode === 'bucket' && activeBucketName ? activeBucketName : 'portfolio';
  const allocAltLabel =
    allocMode === 'bucket' ? 'portfolio' : (activeBucketName ?? 'portfolio');

  return (
    <section class="htable">
      <header class="htable-head">
        <div class="htable-title">Holdings</div>
        <div class="htable-head-right">
          {!bucketFilter && (
            <label
              class="htable-merge-toggle mono"
              title="Combine holdings that track the same underlying index (e.g. QQQ + SXRV → NASDAQ-100)"
            >
              <input
                type="checkbox"
                checked={mergeIdx}
                onChange={(e) => setMergeIdx((e.target as HTMLInputElement).checked)}
              />
              Merge same index
            </label>
          )}
          <select
            class="htable-filter mono"
            value={bucketFilter ?? ''}
            onChange={(e) => onBucketFilterChange((e.target as HTMLSelectElement).value || null)}
          >
            <option value="">All assets</option>
            {sortedBuckets.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <button
            type="button"
            class="htable-add-btn mono"
            onClick={() => setShowAdd(true)}
          >
            Add
          </button>
          <div class="htable-count mono">{displayRows.length} positions</div>
        </div>
      </header>

      {showAdd && (
        <AddHoldingDialog
          buckets={buckets.filter((b) => !(b.id in VIRTUAL_BUCKET_TO_NON_TRADABLE_ID))}
          defaultBucketId={
            bucketFilter && !(bucketFilter in VIRTUAL_BUCKET_TO_NON_TRADABLE_ID)
              ? bucketFilter
              : null
          }
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            onHoldingSaved?.();
          }}
        />
      )}

      <div class="htable-row htable-colhead mono">
        <span class="htable-colhead-label">Holding</span>
        {bucketFilter && activeBucketName ? (
          <button
            type="button"
            class="htable-colhead-label htable-alloc-toggle"
            aria-pressed={allocMode === 'bucket'}
            title={`Switch to % of ${allocAltLabel}`}
            onClick={() =>
              setAllocMode((m) => (m === 'bucket' ? 'portfolio' : 'bucket'))
            }
          >
            Allocation <span class="htable-alloc-toggle-scope">· of {allocScopeLabel}</span>
          </button>
        ) : (
          <span class="htable-colhead-label">Allocation</span>
        )}
        <span class="htable-colhead-label right">Value</span>
        <span class="htable-colhead-label right">{WIN_LABEL[win]}</span>
        <span class="htable-colhead-label right">P&amp;L</span>
      </div>

      {visible.map((r) => {
        const d = r.deltas[WK[win]];
        const dCls = r.value_usd == null ? 'dim' : signClass(d.pct);
        const value = ccy === 'USD' ? r.value_usd : r.value_eur;
        const dAbs = ccy === 'USD' ? d.abs_usd : d.abs_eur;
        const ccyRatio =
          ccy === 'USD'
            ? 1
            : r.value_usd && r.value_eur
              ? r.value_eur / r.value_usd
              : 1;
        const priceDisplay = r.price_usd != null ? r.price_usd * ccyRatio : null;
        const avgCcy = (r.ccy ?? 'USD') as Currency;
        const pnlAbs = r.pnl ? (ccy === 'USD' ? r.pnl.abs_usd : r.pnl.abs_eur) : null;
        const pnlCls = r.pnl ? signClass(r.pnl.pct) : 'dim';
        const isMerged = Array.isArray(r.members);
        const isVirtual = isVirtualRow(r);
        const isManual = r.provider === 'manual';
        const isStablecoin = r.asset_class === 'cash_eq';
        const unit = isCryptoUnit(r) ? '' : ' sh';
        const rawShare = allocMode === 'bucket' ? r.share_in_bucket : r.share_in_portfolio;
        const sharePct = Math.max(0, Math.min(1, rawShare));
        const href = yahooUrl(r);
        const classLabel = r.asset_class ? CLASS_LABEL[r.asset_class] : null;
        const classKey = r.asset_class ? CLASS_KEY[r.asset_class] : null;

        return (
          <div
            class={`htable-row${classKey ? ` htable-class-${classKey}` : ''}`}
            key={isMerged ? `idx:${r.symbol}` : rowKey(r)}
          >
            <div class="htable-cell htable-cell-name">
              <div class="htable-main">
                {href ? (
                  <a
                    class="htable-ticker mono"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {r.symbol}
                  </a>
                ) : (
                  <span class="htable-ticker mono">{r.symbol}</span>
                )}
                <span class="htable-nmlabel">{r.name}</span>
              </div>
              <div class="htable-sub mono htable-edit-host">
                {classLabel && (
                  <span
                    class={`htable-class htable-class-${classKey}`}
                    title={`Asset class: ${classLabel}`}
                  >
                    {classLabel}
                  </span>
                )}
                {isMerged ? (
                  <>
                    {classLabel && ' · '}
                    <span class="htable-merged-members">{r.members!.join(', ')}</span>
                  </>
                ) : isVirtual ? (
                  <>
                    <button
                      type="button"
                      class="htable-edit-trigger"
                      onClick={() => setEditKey(rowKey(r))}
                      aria-label={`Edit ${r.name} estimated value`}
                    >
                      illiquid · est. value
                    </button>
                  </>
                ) : isManual ? (
                  <span class="dim">illiquid · est. value</span>
                ) : (
                  <>
                    {classLabel && ' · '}
                    <button
                      type="button"
                      class="htable-edit-trigger"
                      onClick={() => setEditKey(rowKey(r))}
                      aria-label={`Edit ${r.symbol} quantity${isStablecoin ? '' : ' and average price'}`}
                    >
                      {quantity(r.quantity)}{unit}
                      {!isStablecoin && r.avg_price != null && (
                        <> · avg {amount(r.avg_price, avgCcy, { decimals: 2 })}</>
                      )}
                      {!isStablecoin && r.avg_price == null && (
                        <> · <span class="dim">add avg</span></>
                      )}
                    </button>
                  </>
                )}
                {editKey === rowKey(r) && !isVirtual && (
                  <EditHoldingPopover
                    row={r}
                    onClose={() => setEditKey(null)}
                    onSaved={() => {
                      setEditKey(null);
                      onHoldingSaved?.();
                    }}
                  />
                )}
                {editKey === rowKey(r) && isVirtual && virtualNonTradableId(r) && (
                  <EditNonTradablePopover
                    id={virtualNonTradableId(r)!}
                    name={r.name}
                    valueEur={r.value_eur}
                    onClose={() => setEditKey(null)}
                    onSaved={() => {
                      setEditKey(null);
                      onHoldingSaved?.();
                    }}
                  />
                )}
              </div>
            </div>

            <div class="htable-cell htable-cell-alloc">
              <span class="htable-mlabel">Allocation</span>
              <div
                class="htable-alloc-bar"
                role="img"
                aria-label={`${fmtPct(sharePct, { decimals: 2 })} of ${allocScopeLabel}`}
              >
                <span
                  class="htable-alloc-fill"
                  style={`width: ${(sharePct * 100).toFixed(2)}%`}
                />
              </div>
              {bucketFilter && activeBucketName ? (
                <button
                  type="button"
                  class="htable-alloc-pct mono htable-alloc-pct-toggle"
                  aria-pressed={allocMode === 'bucket'}
                  title={`Switch to % of ${allocAltLabel}`}
                  onClick={() =>
                    setAllocMode((m) => (m === 'bucket' ? 'portfolio' : 'bucket'))
                  }
                >
                  {fmtPct(sharePct, { decimals: 2 })}
                </button>
              ) : (
                <span class="htable-alloc-pct mono">
                  {fmtPct(sharePct, { decimals: 2 })}
                </span>
              )}
            </div>

            <div class="htable-cell right">
              <div class="htable-main mono">
                {value != null ? amount(value, ccy) : '—'}
              </div>
              <div class="htable-sub mono">
                {!isManual && priceDisplay != null
                  ? `@ ${amount(priceDisplay, ccy, { decimals: 2 })}`
                  : ''}
              </div>
            </div>
            <div class={`htable-cell htable-cell-change right ${isManual ? 'dim' : dCls}`}>
              <div class="htable-main mono">
                <span class="htable-mlabel">{WIN_LABEL[win]}</span>
                {isManual || r.value_usd == null ? '—' : fmtSignedPct(d.pct)}
              </div>
              <div class="htable-sub mono">
                {!isManual && r.value_usd != null ? amount(dAbs, ccy, { signed: true }) : ''}
              </div>
            </div>
            <div class={`htable-cell htable-cell-pnl right ${pnlCls}`}>
              <div class="htable-main mono">
                <span class="htable-mlabel">P&amp;L</span>
                {r.pnl && pnlAbs != null ? amount(pnlAbs, ccy, { signed: true }) : '—'}
              </div>
              <div class="htable-sub mono">
                {r.pnl ? fmtSignedPct(r.pnl.pct) : ''}
              </div>
            </div>
          </div>
        );
      })}

      {canExpand && (
        <div class="htable-footer">
          <button
            class="htable-loadall"
            onClick={() => setExpanded((x) => !x)}
          >
            {expanded
              ? `Show top ${INITIAL}`
              : `Load all ${filtered.length}`}
          </button>
        </div>
      )}
    </section>
  );
}
